import {
    Flex,
    useBreakpointValue, Text, Button
} from "@chakra-ui/react";
import { React, useEffect, useState, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import EventsService from '../../api/services/EventsService';
import moment from 'moment';
import { useTranslation } from "react-i18next";
import { registerLocale } from "react-datepicker";
import en from "date-fns/locale/en-GB";
import pl from "date-fns/locale/pl";
import "react-datepicker/dist/react-datepicker.css";
import { Trans } from 'react-i18next';
import useFavorites from './useFavorites';
import { useAppToast } from "../../hooks/useAppToast";
import SearchHeader from "../../components/events/SearchHeader";
import EventsList from "../../components/events/EventsList";
import CTASection from '../../components/cta/CTASection';
import MobileHashtagsCarousel from '../../components/events/MobileHashtagsCarousel';
import SectionTitle from '../../components/common/SectionTitle';

registerLocale("en", en);
registerLocale("pl", pl);

const AllEventsPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("default");
    const isMobile = useBreakpointValue({ base: true, md: false });
    const [events, setEvents] = useState([]);
    const { favorites, toggleFavorite } = useFavorites(events, setEvents);
    const [loading, setLoading] = useState(true);
    const [location, setLocation] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [sort] = useState('date_asc');
    const [currentPage] = useState(1);

    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    const toast = useAppToast();

    const [availableHashtags, setAvailableHashtags] = useState([]);
    const [searchCategories, setSearchCategories] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        try {
            const res = await EventsService.getAllEvents({
                page: currentPage,
                dateFrom,
                dateTo,
                sort,
                location
            });
            setEvents(res.data.data.items);

            const hashtagsResponse = await EventsService.getHashtags();
            setAvailableHashtags(hashtagsResponse.data.data.map(h => ({ id: h.id, name: h.name, count: h.count })));

            const categoriesResponse = await EventsService.getCategories();
            setSearchCategories(categoriesResponse.data.data.map(c => ({ id: c.id, name: c.name })));

        } catch (error) {
            // Log do konsoli zamiast toast, jeśli chcemy totalnej stabilności, ale tutaj po prostu usuniemy z zależności
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, dateFrom, dateTo, sort, location]); // Usunięto t i toast

    const handleSearch = () => {
        const eventIds = selectedTags.filter(t => t.type === 'Wydarzenie').map(t => t.id);
        const categoryIds = selectedTags.filter(t => t.type === 'Kategoria').map(t => t.id);
        const hashtagIds = selectedTags.filter(t => t.type === 'Hashtag').map(t => t.id);

        const queryParams = new URLSearchParams();
        if (eventIds.length > 0) {
            queryParams.append('event_ids', eventIds.join(','));
        }
        if (categoryIds.length > 0) {
            queryParams.append('category_ids', categoryIds.join(','));
        }
        if (hashtagIds.length > 0) {
            queryParams.append('hashtag_ids', hashtagIds.join(','));
        }

        if (location) {
            queryParams.append('location', location);
        }
        if (dateFrom) {
            queryParams.append('dateFrom', dateFrom);
        }
        if (dateTo) {
            queryParams.append('dateTo', dateTo);
        }

        navigate(`/results?${queryParams.toString()}`);
    };

    useEffect(() => {
        fetchEvents();
        const interval = setInterval(() => {
            fetchEvents();
        }, 30000);

        return () => clearInterval(interval);
    }, [currentPage, dateFrom, dateTo, sort, location, fetchEvents]); // Reagujemy na konkretne parametry

    const handleHashtagClick = (hashtag) => {
        navigate(`/results?hashtag_ids=${hashtag.id}`);
    };

    useEffect(() => {
        if (startDate) {
            setDateFrom(moment(startDate).format("YYYY-MM-DD"));
        } else {
            setDateFrom("");
        }

        if (endDate) {
            setDateTo(moment(endDate).format("YYYY-MM-DD"));
        } else {
            setDateTo("");
        }
    }, [startDate, endDate]);

    return (
        <Flex flexDirection={'column'} w={"100%"} >
            <SearchHeader
                events={events}
                categories={searchCategories}
                hashtags={availableHashtags}
                onSelectionChange={setSelectedTags}
                selectedTags={selectedTags} // Pass state
                dateRange={dateRange}
                onDateChange={setDateRange}
                location={location}
                onLocationChange={(opt) => setLocation(opt)}
                onSearch={handleSearch}
            />

            <Flex alignItems={"center"} justifyContent="center" w={"100%"} flexDirection={'column'} bg={'surface.card'}>
                <Flex flexDirection={'column'} w="100%" maxW="7xl" px={{ base: '16px', md: '24px', '2xl': 0 }} alignItems={'flex-start'} pt={{ base: '8px', md: '16px' }} pb={{ base: '40px', md: '72px' }}>
                    <EventsList
                        title={t('check.news')}
                        events={events.slice(0, 4)}
                        loading={loading}
                        onFavoriteToggle={toggleFavorite}
                    />
                </Flex>
            </Flex>

            {favorites?.length > 0 && <Flex alignItems={"center"} justifyContent="center" w={"100%"} flexDirection={'column'} bg={'brand.700'}>
                <Flex flexDirection={'column'} w="100%" maxW="7xl" px={{ base: '16px', md: '24px', '2xl': 0 }} alignItems={'flex-start'} py={{ base: '40px', md: '72px' }}>
                    <SectionTitle color="#FFFFFF" borderColor="#5A1E30" display="flex" alignItems="center" gap="10px">
                        {t('event.favorite')}
                        <svg width="22" height="19" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M3.17159 2.70102C2.80015 3.08179 2.50551 3.53382 2.30449 4.03132C2.10346 4.52882 2 5.06203 2 5.60052C2 6.13901 2.10346 6.67222 2.30449 7.16972C2.50551 7.66722 2.80015 8.11925 3.17159 8.50002L10 15.5L16.8284 8.50002C17.5786 7.73102 18 6.68804 18 5.60052C18 4.513 17.5786 3.47002 16.8284 2.70102C16.0783 1.93203 15.0609 1.50001 14 1.50001C12.9391 1.50001 11.9217 1.93203 11.1716 2.70102L10 3.90201L8.82845 2.70102C8.45702 2.32025 8.01606 2.01821 7.53076 1.81214C7.04546 1.60606 6.52531 1.5 6.00002 1.5C5.47473 1.5 4.95458 1.60606 4.46928 1.81214C3.98398 2.01821 3.54302 2.32025 3.17159 2.70102Z" stroke="#F4B8C5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </SectionTitle>
                    <EventsList
                        events={favorites}
                        loading={loading}
                        onFavoriteToggle={toggleFavorite}
                        noEventsMessage={t('event.not_found')}
                    />
                </Flex>
            </Flex>}

            <Flex alignItems={"center"} justifyContent="center" w={"100%"} flexDirection={'column'} bg={'surface.page'}>
                <Flex flexDirection={'column'} w="100%" maxW="7xl" px={{ base: '16px', md: '24px', '2xl': 0 }} alignItems={'flex-start'} py={{ base: '40px', md: '64px' }} pb={{ base: '40px', md: '72px' }} id="events">
                    <EventsList
                        title={t('event.popular')}
                        events={events}
                        loading={loading}
                        onFavoriteToggle={toggleFavorite}
                    />
                </Flex>
            </Flex>

            <CTASection />

            <Flex alignItems="center" justifyContent="center" w={"100%"} flexDirection={'column'} bg={'surface.muted'}>
                <Flex flexDirection={'column'} w="100%" maxW="7xl" px={{ base: '16px', md: '24px', '2xl': 0 }} alignItems={'flex-start'} py={{ base: '36px', md: '56px' }}>
                    <SectionTitle borderColor="#E6D5DA">
                        <Trans i18nKey="event.hashtags" components={{ b: <b /> }} />
                    </SectionTitle>
                    {isMobile ? (
                        <MobileHashtagsCarousel 
                            hashtags={availableHashtags.slice(0, 10)} 
                            onClick={handleHashtagClick} 
                        />
                    ) : (
                        <Flex gap={{ base: '8px', md: '16px' }}
                            flexWrap="wrap"
                            justify={'flex-start'}
                            w={'100%'}
                            mb={{ base: '16px', md: '0' }}
                            maxH={{ base: '104px', md: '104px' }}
                            overflow="hidden"
                        >
                            {availableHashtags.map((hashtag) => (
                                <Button
                                    key={hashtag.id}
                                    bg={'surface.card'}
                                    border={'1px solid'}
                                    borderColor={'brand.200'}
                                    px={'18px'}
                                    h={'44px'}
                                    borderRadius={'full'}
                                    w={'auto'}
                                    _hover={{ bg: 'brand.100' }}
                                    onClick={() => handleHashtagClick(hashtag)}
                                >
                                    <Text fontWeight={'600'} fontSize='14px' color={'brand.700'}>
                                        #{hashtag.name.replace(/^#+/, '')}
                                    </Text>
                                </Button>
                            ))}
                        </Flex>
                    )}

                </Flex>
            </Flex>
        </Flex>
    )
}

export default AllEventsPage;
